import { tt } from '@agos/shared'

/**
 * `/opening-hours` — when the restaurant is open, and until when it takes orders.
 *
 * **FB-133.** Legacy saves like this:
 *
 * ```tsx
 * await fetch(`${API_BASE}/api/settings/opening-hours`, { method: "PUT", … });
 * localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
 * setSaved(true);
 * ```
 *
 * No `res.ok`, so a refusal shows the tick — and then the browser copy is written anyway, so the next
 * load reads back hours the server never took. `loadOpeningHours()` prefers that copy over the server.
 *
 * **FB-134.** The *reset* button sets hard-coded defaults that are not this restaurant's hours. On the
 * restored data every day has an `orderCutoff` of **21:47** and Sunday is open **12:00–22:00**;
 * `DEFAULT_DAYS` has **no cutoff at all** and Sunday **closed**. So one click extends order-taking to
 * 22:00 — thirteen minutes past the kitchen's own cutoff — and closes a trading day, with no
 * confirmation and nothing on screen to say what changed.
 *
 * The cutoff is not decoration: it is what decides whether an order is accepted.
 *
 * ```tsx
 * const cutoffStr = day.orderCutoff && day.orderCutoff.trim() ? day.orderCutoff : day.to;
 * ```
 */

export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

export const DAY_KEYS: readonly DayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

export function dayLabel(key: DayKey): string {
  if (key === 'mon') return tt('Pondelok')
  if (key === 'tue') return tt('Utorok')
  if (key === 'wed') return tt('Streda')
  if (key === 'thu') return tt('Štvrtok')
  if (key === 'fri') return tt('Piatok')
  if (key === 'sat') return tt('Sobota')
  return tt('Nedeľa')
}

export interface DayHours {
  open?: boolean
  from?: string
  to?: string
  /** When the kitchen stops accepting orders. Absent means *at closing time*. */
  orderCutoff?: string
}

export interface Announcement {
  enabled?: boolean
  message?: string
  showAlways?: boolean
}

export interface OpeningHours {
  days?: Partial<Record<DayKey, DayHours>>
  emergencyClosed?: boolean
  announcement?: Announcement
}

export interface Day {
  key: DayKey
  open: boolean
  from: string
  to: string
  /** `null` when there is none, which means orders are taken until closing. */
  orderCutoff: string | null
}

export interface HoursForm {
  days: Day[]
  emergencyClosed: boolean
  announcementEnabled: boolean
  announcementMessage: string
  announcementAlways: boolean
}

const FALLBACK_FROM = '08:00'
const FALLBACK_TO = '22:00'

export function formOf(hours: OpeningHours | undefined): HoursForm {
  return {
    days: DAY_KEYS.map((key) => {
      const day = hours?.days?.[key]
      return {
        key,
        // A day with nothing stored is open, not shut — a missing record must not close a restaurant.
        open: day?.open !== false,
        from: day?.from ?? FALLBACK_FROM,
        to: day?.to ?? FALLBACK_TO,
        orderCutoff: (day?.orderCutoff ?? '').trim() === '' ? null : (day?.orderCutoff ?? null),
      }
    }),
    emergencyClosed: hours?.emergencyClosed === true,
    announcementEnabled: hours?.announcement?.enabled === true,
    announcementMessage: hours?.announcement?.message ?? '',
    announcementAlways: hours?.announcement?.showAlways === true,
  }
}

/**
 * `PUT /settings/opening-hours` replaces the section whole, and the backend says why:
 *
 * > Replaced whole: the screen sends every day, and a merge would make a closed day unclosable.
 *
 * So every day goes, always. An absent cutoff is sent as absent rather than as an empty string, because
 * the consumer tests `orderCutoff && orderCutoff.trim()`.
 */
export function payloadOf(form: HoursForm): OpeningHours {
  const days: Partial<Record<DayKey, DayHours>> = {}
  for (const day of form.days) {
    days[day.key] = {
      open: day.open,
      from: day.from,
      to: day.to,
      ...(day.orderCutoff === null ? {} : { orderCutoff: day.orderCutoff }),
    }
  }
  return {
    days,
    emergencyClosed: form.emergencyClosed,
    announcement: {
      enabled: form.announcementEnabled,
      message: form.announcementMessage.trim(),
      showAlways: form.announcementAlways,
    },
  }
}

// ─── the times themselves ────────────────────────────────────────────────────

const TIME_SHAPE = /^([01]\d|2[0-3]):([0-5]\d)$/

export function minutesOf(time: string): number | null {
  const match = TIME_SHAPE.exec(time.trim())
  if (match === null) return null
  return Number(match[1]) * 60 + Number(match[2])
}

export type DayProblem = 'from-shape' | 'to-shape' | 'cutoff-shape' | 'inverted' | 'cutoff-outside' | null

/**
 * What is wrong with a day, if anything.
 *
 * `inverted` is a closing time before the opening one, which legacy accepts and stores — and which the
 * availability check then reads as *never open*. `cutoff-outside` is a cutoff that is not inside the
 * opening hours, which means either the restaurant stops taking orders before it opens or the cutoff
 * does nothing at all.
 */
export function dayProblem(day: Day): DayProblem {
  if (!day.open) return null
  const from = minutesOf(day.from)
  const to = minutesOf(day.to)
  if (from === null) return 'from-shape'
  if (to === null) return 'to-shape'
  if (to <= from) return 'inverted'
  if (day.orderCutoff !== null) {
    const cutoff = minutesOf(day.orderCutoff)
    if (cutoff === null) return 'cutoff-shape'
    if (cutoff < from || cutoff > to) return 'cutoff-outside'
  }
  return null
}

export function dayProblemMessage(problem: DayProblem): string {
  if (problem === 'inverted') {
    return tt('Zatvára skôr než otvára - takto sa v tento deň neotvorí vôbec.')
  }
  if (problem === 'cutoff-outside') {
    return tt('Koniec príjmu objednávok je mimo otváracích hodín.')
  }
  if (problem === null) return ''
  return tt('Neplatný čas. Použite tvar 08:00.')
}

export function problems(form: HoursForm): { key: DayKey; problem: DayProblem }[] {
  return form.days
    .map((day) => ({ key: day.key, problem: dayProblem(day) }))
    .filter((entry) => entry.problem !== null)
}

/**
 * How long a day accepts orders for — the figure the cutoff actually changes.
 *
 * `null` for a day that does not make sense, which includes an inverted pair *and* a cutoff outside the
 * hours: measuring a window from a cutoff the restaurant is not open for would answer a plausible number
 * about a day it will never trade.
 */
export function orderingMinutes(day: Day): number | null {
  if (!day.open) return 0
  if (dayProblem(day) !== null) return null
  const from = minutesOf(day.from)
  const until = minutesOf(day.orderCutoff ?? day.to)
  if (from === null || until === null || until <= from) return null
  return until - from
}

/**
 * The time after which no order is taken — `orderCutoff` when set, closing time otherwise.
 *
 * This is legacy's own rule, lifted so the page can show which of the two is in force. A cutoff blank on
 * screen does not mean *no cutoff*; it means *the closing time is the cutoff*, and those read very
 * differently to somebody deciding whether to shorten the evening.
 */
export function effectiveCutoff(day: Day): { time: string; source: 'cutoff' | 'closing' } {
  if (day.orderCutoff !== null) return { time: day.orderCutoff, source: 'cutoff' }
  return { time: day.to, source: 'closing' }
}

// ─── FB-134: what reset would do ─────────────────────────────────────────────

/**
 * Legacy's hard-coded defaults, kept only to say what pressing *reset* would change.
 *
 * They are not this restaurant's hours and never were: no cutoff on any day, and Sunday closed.
 */
export const LEGACY_DEFAULTS: Readonly<Record<DayKey, DayHours>> = {
  mon: { open: true, from: '08:00', to: '22:00' },
  tue: { open: true, from: '08:00', to: '22:00' },
  wed: { open: true, from: '08:00', to: '22:00' },
  thu: { open: true, from: '08:00', to: '22:00' },
  fri: { open: true, from: '08:00', to: '23:00' },
  sat: { open: true, from: '10:00', to: '23:00' },
  sun: { open: false, from: '10:00', to: '20:00' },
}

export interface ResetChange {
  key: DayKey
  kind: 'closes-day' | 'opens-day' | 'loses-cutoff' | 'changes-hours'
  before: string
  after: string
}

/**
 * Every difference a reset would make, so it can be shown before it is made.
 *
 * On the restored data that is seven lost cutoffs and one closed day — a restaurant that took orders
 * until 21:47 now taking them until 22:00, and a Sunday it trades on marked shut.
 */
export function resetChanges(form: HoursForm): ResetChange[] {
  const changes: ResetChange[] = []
  for (const day of form.days) {
    const target = LEGACY_DEFAULTS[day.key]
    const targetOpen = target.open === true

    if (day.open && !targetOpen) {
      changes.push({
        key: day.key,
        kind: 'closes-day',
        before: `${day.from}–${day.to}`,
        after: tt('zatvorené'),
      })
      continue
    }
    if (!day.open && targetOpen) {
      changes.push({
        key: day.key,
        kind: 'opens-day',
        before: tt('zatvorené'),
        after: `${target.from ?? ''}–${target.to ?? ''}`,
      })
      continue
    }
    if (!day.open) continue

    if (day.orderCutoff !== null) {
      changes.push({
        key: day.key,
        kind: 'loses-cutoff',
        before: day.orderCutoff,
        after: target.to ?? day.to,
      })
    }
    if (day.from !== target.from || day.to !== target.to) {
      changes.push({
        key: day.key,
        kind: 'changes-hours',
        before: `${day.from}–${day.to}`,
        after: `${target.from ?? ''}–${target.to ?? ''}`,
      })
    }
  }
  return changes
}

export function resetForm(form: HoursForm): HoursForm {
  return {
    ...form,
    days: DAY_KEYS.map((key) => {
      const target = LEGACY_DEFAULTS[key]
      return {
        key,
        open: target.open === true,
        from: target.from ?? FALLBACK_FROM,
        to: target.to ?? FALLBACK_TO,
        orderCutoff: null,
      }
    }),
  }
}

export function changeKindLabel(kind: ResetChange['kind']): string {
  if (kind === 'closes-day') return tt('zatvorí deň')
  if (kind === 'opens-day') return tt('otvorí deň')
  if (kind === 'loses-cutoff') return tt('zruší koniec príjmu objednávok')
  return tt('zmení hodiny')
}

// ─── copying Monday across ───────────────────────────────────────────────────

/** Legacy's *copy Monday to every day* — including the cutoff, which legacy also copies. */
export function copyMondayAcross(form: HoursForm): HoursForm {
  const monday = form.days.find((day) => day.key === 'mon')
  if (monday === undefined) return form
  return {
    ...form,
    days: form.days.map((day) =>
      day.key === 'mon'
        ? day
        : { key: day.key, open: monday.open, from: monday.from, to: monday.to, orderCutoff: monday.orderCutoff },
    ),
  }
}

// ─── what the customer sees ──────────────────────────────────────────────────

export interface ShopSummary {
  openDays: number
  /** True when nothing at all is open, which is a state worth naming before it is saved. */
  closedAllWeek: boolean
  emergencyClosed: boolean
  daysWithCutoff: number
}

export function summarise(form: HoursForm): ShopSummary {
  const openDays = form.days.filter((day) => day.open).length
  return {
    openDays,
    closedAllWeek: openDays === 0,
    emergencyClosed: form.emergencyClosed,
    daysWithCutoff: form.days.filter((day) => day.orderCutoff !== null).length,
  }
}

/** An emergency close overrides every day, so it is said once and loudly. */
export function customerFacingState(form: HoursForm): 'emergency' | 'closed-week' | 'open' {
  if (form.emergencyClosed) return 'emergency'
  if (summarise(form).closedAllWeek) return 'closed-week'
  return 'open'
}

export function customerFacingMessage(state: ReturnType<typeof customerFacingState>): string {
  if (state === 'emergency') {
    return tt('Mimoriadne zatvorené - otváracie hodiny sa ignorujú a zákazník si neobjedná.')
  }
  if (state === 'closed-week') {
    return tt('Žiaden deň nie je otvorený - zákazník si neobjedná v žiadny deň.')
  }
  return tt('Otváracie hodiny platia.')
}
